import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_EVENTPLATFORM_API ?? 'http://localhost:8161';

export type DLQMessage = {
  id: string;
  event_id: string;
  event_name: string;
  tenant_id: string;
  failure_category: string;
  failure_reason: string;
  attempts: number;
  replay_eligible: boolean;
  status: string;
  created_at: string;
};

export type ReplayJob = {
  id: string;
  status: string;
  total_count: number;
  replayed: number;
  failed: number;
  dry_run: boolean;
  created_at: string;
};

export type RetryPolicy = {
  id: string;
  name: string;
  max_attempts: number;
  strategy: string;
  enabled: boolean;
};

export type MessagingDashboard = {
  dlq_total: number;
  dlq_pending: number;
  replay_jobs_active: number;
  retry_total: number;
  failure_by_category: Record<string, number>;
  recovery_pending: number;
};

function fetchMessaging<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(BASE, path, init);
}

export const messagingApi = {
  dashboard: () => fetchMessaging<MessagingDashboard>('/api/v1/messaging/dashboard'),

  listDLQ: (params?: { tenant_id?: string; event_type?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.tenant_id) q.set('tenant_id', params.tenant_id);
    if (params?.event_type) q.set('event_type', params.event_type);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return fetchMessaging<{ messages: DLQMessage[]; count: number }>(`/api/v1/dlq${qs ? `?${qs}` : ''}`);
  },

  getDLQ: (id: string) => fetchMessaging<DLQMessage>(`/api/v1/dlq/${id}`),

  replayOne: (id: string, dryRun = false) =>
    fetchMessaging<{ data: ReplayJob }>(`/api/v1/dlq/${id}/replay`, {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun }),
    }),

  replayBatch: (filter: Record<string, unknown>) =>
    fetchMessaging<{ data: ReplayJob }>('/api/v1/dlq/replay', {
      method: 'POST',
      body: JSON.stringify(filter),
    }),

  replayJobs: (limit = 50) =>
    fetchMessaging<{ jobs: ReplayJob[] }>(`/api/v1/replay/jobs?limit=${limit}`),

  replayHistory: (limit = 100) =>
    fetchMessaging<{ history: unknown[] }>(`/api/v1/replay/history?limit=${limit}`),

  retryPolicies: (tenantId?: string) =>
    fetchMessaging<{ policies: RetryPolicy[] }>(
      `/api/v1/retry/policies${tenantId ? `?tenant_id=${tenantId}` : ''}`,
    ),
};
